/* eslint react-hooks/exhaustive-deps: "warn" */

import { useState, type KeyboardEvent, type MouseEvent } from 'react';

import cn from 'classnames';

import { Icon } from 'ui/atoms/Icon/Icon';

import { DATA_BLOCKS } from 'features/product-pricing/organisms/PricingDataBlocks/PricingDataBlocks.data';

import styles from './PricingDataPicker.module.scss';

const ExternalLinkIcon = () => (
  <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false">
    <path d="M5 2h7v7h-2V5.4L4.4 11 3 9.6 8.6 4H5z" fill="currentColor" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    className={className}
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

interface Props {
  selected: Record<string, boolean>;
  onToggle: (key: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  className?: string;
}

export const PricingDataPicker = ({ selected, onToggle, onSelectAll, onDeselectAll, className }: Props) => {
  const [activeKey, setActiveKey] = useState(DATA_BLOCKS[0].key);

  const handleRowClick = (_event: MouseEvent<HTMLButtonElement>, key: string) => {
    onToggle(key);
    setActiveKey(key);
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLButtonElement>, key: string, index: number) => {
    if (event.key === ' ') {
      event.preventDefault();
      onToggle(key);
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    event.preventDefault();

    const nextIndex = (index + (event.key === 'ArrowDown' ? 1 : -1) + DATA_BLOCKS.length) % DATA_BLOCKS.length;
    const nextKey = DATA_BLOCKS[nextIndex].key;

    setActiveKey(nextKey);
    document.getElementById(`pricing-data-tab-${nextKey}`)?.focus();
  };

  const activeIndex = Math.max(
    0,
    DATA_BLOCKS.findIndex((block) => block.key === activeKey),
  );
  const activeBlock = DATA_BLOCKS[activeIndex];

  return (
    <div className={cn(styles.configurator, className)}>
      <div className={styles.rail} role="group" aria-label="Data blocks - tick the ones you need">
        <div className={styles.railHead}>
          <span className={styles.railHint}>
            <span className={styles.railHintDot} aria-hidden="true" />
            Tick to include &middot; click to read
          </span>

          <span className={styles.railActions}>
            <button
              type="button"
              className={cn(styles.railAction, styles.railActionPrimary)}
              onClick={onSelectAll}
            >
              <CheckIcon className={styles.railActionIcon} />
              Select all
            </button>
            <button type="button" className={cn(styles.railAction, styles.railActionGhost)} onClick={onDeselectAll}>
              <Icon type="close-5" className={cn(styles.railActionIcon, styles.railActionIconFill)} />
              Deselect all
            </button>
          </span>
        </div>

        {DATA_BLOCKS.map((block, index) => {
          const isOn = Boolean(selected[block.key]);
          const isViewed = block.key === activeKey;

          return (
            <div key={block.key} className={cn(styles.row, isOn && !isViewed && styles.rowOn, isViewed && styles.rowViewed)}>
              <button
                type="button"
                id={`pricing-data-tab-${block.key}`}
                className={styles.tab}
                aria-pressed={isOn}
                aria-controls={`pricing-data-panel-${block.key}`}
                onClick={(event) => handleRowClick(event, block.key)}
                onKeyDown={(event) => handleRowKeyDown(event, block.key, index)}
              >
                <span className={cn(styles.box, isOn && styles.boxOn)} aria-hidden="true">
                  {isOn && <CheckIcon className={styles.boxIcon} />}
                </span>

                <span className={styles.number}>{String(index + 1).padStart(2, '0')}</span>
                <span className={styles.label}>{block.label}</span>
                <span className={cn(styles.count, isOn && styles.countOn)}>{block.count}</span>
              </button>

              <a
                className={styles.link}
                href={block.href}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={-1}
                aria-label={`Open the ${block.label} page in a new tab`}
              >
                <ExternalLinkIcon />
              </a>
            </div>
          );
        })}
      </div>

      <div className={styles.panel}>
        <div className={styles.panelBody} key={activeBlock.key}>
          <div className={styles.panelTop}>
            <span className={styles.panelEyebrow}>
              {String(activeIndex + 1).padStart(2, '0')}
              <em>/{DATA_BLOCKS.length}</em>
            </span>

            <div>
              <h3 className={styles.panelTitle}>{activeBlock.label}</h3>
              <p className={styles.panelTagline}>{activeBlock.tagline}</p>
            </div>
          </div>

          <ul className={styles.fieldList} id={`pricing-data-panel-${activeBlock.key}`}>
            {activeBlock.fields.map((field) => (
              <li key={field}>{field}</li>
            ))}
          </ul>

          <p className={styles.panelFooter}>
            <a href="https://www.globaldatabase.com/data-dictionary">Full data dictionary</a> &middot;{' '}
            <a href={activeBlock.href}>See this block in use</a>
          </p>
        </div>
      </div>
    </div>
  );
};
